import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Typography,
  Button,
  List,
  ListItem,
  TextField,
  IconButton,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';

const CodebookLibraryPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const initialCodeBooks = location.state?.codeBookLibrary?.codebooks || [];
  const { t } = useTranslation();

  const [codebooks, setCodebooks] = useState(initialCodeBooks);
  const [selectedCodebookId, setSelectedCodebookId] = useState('');
  const [editingCodebook, setEditingCodebook] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Arama fonksiyonu
  const filteredCodebooks = codebooks.filter(cb =>
    cb.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cb.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const generateNewId = () => {
    if (codebooks.length === 0) return 'cb1';
    const maxId = Math.max(...codebooks.map(cb => {
      const numericId = parseInt(cb.id.replace('cb', ''), 10);
      return isNaN(numericId) ? 0 : numericId;
    }));
    return `cb${maxId + 1}`;
  };

  const handleNewCodebook = () => {
    const newCodebook = {
      id: generateNewId(),
      name: t('codebookLibrary.newCodebookDefaultName', { number: codebooks.length + 1 }),
      description: '',
      codes: [],
    };
    setEditingCodebook(newCodebook);
    setSelectedCodebookId('');
  };

  const handleSelectCodebook = (codebook) => {
    setSelectedCodebookId(codebook.id);
    setEditingCodebook({ ...codebook });
  };

  const addCode = () => {
    let nextCode = 1;
    if (editingCodebook.codes.length > 0) {
      const lastCode = editingCodebook.codes[editingCodebook.codes.length - 1].code;
      const lastCodeNumber = parseInt(lastCode, 10);
      if (!isNaN(lastCodeNumber)) {
        nextCode = lastCodeNumber + 1;
      }
    }
    
    setEditingCodebook((prev) => ({
      ...prev,
      codes: [...prev.codes, { code: nextCode.toString(), text: '' }],
    }));
  };

  const handleCodeInput = (index, value) => {
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    updateCode(index, 'code', sanitizedValue);
  };

  const updateCode = (index, field, value) => {
    setEditingCodebook((prev) => {
      const newCodes = [...prev.codes];
      newCodes[index][field] = value;
      return { ...prev, codes: newCodes };
    });
  };

  const deleteCode = (index) => {
    setEditingCodebook((prev) => ({
      ...prev,
      codes: prev.codes.filter((_, i) => i !== index),
    }));
  };

  const handleCancel = () => {
    setEditingCodebook(null);
    setSelectedCodebookId('');
  };

  const handleDeleteConfirmation = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCodebook = async () => {
    setDeleteConfirmOpen(false);
    if (!currentUser?.email) return;

    setIsLoading(true);
    try {
      const updatedCodebooks = codebooks.filter(cb => cb.id !== editingCodebook.id);

      const formData = new FormData();
      formData.append('useremail', currentUser.email);
      formData.append('codeBookLibrary', JSON.stringify({ codebooks: updatedCodebooks }));

      const response = await fetch('https://codini-porejct-codebookupdate-gt-795950092325.europe-west3.run.app', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error(t('codebookLibrary.errorFailedToDelete'));

      const responseData = await response.json();
      const updatedFromServer = responseData.codeBookLibrary?.codebooks || updatedCodebooks;
      
      setCodebooks(updatedFromServer);
      handleCancel();
      setSnackbar({ open: true, message: t('codebookLibrary.codebookDeleted'), severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const saveCodebook = async () => {
    if (!currentUser?.email) return;

    setIsLoading(true);
    try {
      const isExisting = codebooks.some(cb => cb.id === editingCodebook.id);
      const updatedCodebooks = isExisting 
        ? codebooks.map(cb => cb.id === editingCodebook.id ? editingCodebook : cb) 
        : [...codebooks, editingCodebook];

      const formData = new FormData();
      formData.append('useremail', currentUser.email);
      formData.append('codeBookLibrary', JSON.stringify({ codebooks: updatedCodebooks }));

      const response = await fetch('https://codini-porejct-codebookupdate-gt-795950092325.europe-west3.run.app', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error(t('codebookLibrary.errorFailedToSave'));

      const responseData = await response.json();
      const updatedFromServer = responseData.codeBookLibrary?.codebooks || updatedCodebooks;
      
      setCodebooks(updatedFromServer);
      setEditingCodebook(updatedFromServer.find(cb => cb.id === editingCodebook.id));
      setSelectedCodebookId(editingCodebook.id);
      setSnackbar({ open: true, message: t('codebookLibrary.codebookSaved'), severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#f4f4f4', minHeight: '100vh', padding: '20px' }}>
      <Container sx={{ mt: 1, backgroundColor: '#fff', borderRadius: 2, padding: '3px' }}>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 2 }}>
          <Typography variant="h5" gutterBottom>
            {t('codebookLibrary.title')}
          </Typography>
          <Button variant="outlined" onClick={() => navigate('/ProjectsPage')}>
            {t('cancel')}
          </Button>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNewCodebook}
          disabled={isLoading}
          sx={{
            padding: '5px 10px',
            fontSize: '12px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            backgroundColor: '#ff7b00',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#8ecae6',
            },
          }}
        >
          {t('codebookLibrary.newCodebook')}
        </Button>
        
        {/* Arama Çubuğu ve Liste Görünümü */}
        <Box sx={{ mt: 2, mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            label={t('codebookLibrary.searchCodebooks')}
            variant="outlined"
            size="small"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 1 }}
          />
          <List sx={{ 
            border: '1px solid #ddd', 
            borderRadius: 1,
            maxHeight: 300,
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: 8 },
            '&::-webkit-scrollbar-track': { bgcolor: '#f5f5f5' },
            '&::-webkit-scrollbar-thumb': { 
              bgcolor: '#888',
              borderRadius: 2,
              '&:hover': { bgcolor: '#666' }
            }
          }}>
            {filteredCodebooks.map((cb) => (
              <ListItem
                button
                key={cb.id}
                onClick={() => !isLoading && handleSelectCodebook(cb)}
                selected={selectedCodebookId === cb.id}
                disabled={isLoading}
                sx={{
                  '&:hover': { backgroundColor: !isLoading ? '#f5f5f5' : 'none' },
                  '&.Mui-selected': { backgroundColor: '#e0e0e0' }
                }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Typography fontWeight="medium">{cb.name}</Typography>
                  {cb.description && (
                    <Typography variant="body2" color="textSecondary" noWrap>
                      {cb.description}
                    </Typography>
                  )}
                </Box>
                {isLoading && cb.id === editingCodebook?.id && (
                  <CircularProgress size={20} sx={{ ml: 2 }} />
                )}
              </ListItem>
            ))}
          </List>
        </Box>

        {editingCodebook && (
          <Box sx={{ mb: 2, background: '#e9ecef' }}>
            <TextField
              label={t('codebookLibrary.name')}
              value={editingCodebook.name}
              onChange={(e) => setEditingCodebook(p => ({ ...p, name: e.target.value }))}
              sx={{ width: '95%', ml: 2, mt: 2, mb: 2, background: '#fff' }}
              disabled={isLoading}
            />
            <TextField
              label={t('codebookLibrary.description')}
              value={editingCodebook.description}
              onChange={(e) => setEditingCodebook(p => ({ ...p, description: e.target.value }))}
              multiline
              sx={{ width: '95%', ml: 2, mt: 2, mb: 2, background: '#fff' }}
              disabled={isLoading}
            />

            <Typography variant="h6" sx={{ ml: 2 }}>
              {t('codebookLibrary.codebookList')}
            </Typography>
            <List>
              {editingCodebook.codes.map((code, index) => (
                <ListItem key={index} sx={{ gap: 1 }}>
                  <TextField
                    label={t('codebookLibrary.code')}
                    value={code.code}
                    onChange={(e) => handleCodeInput(index, e.target.value)}
                    size="small"
                    disabled={isLoading}
                    sx={{ background: '#fff' }}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                    }}
                  />
                  <TextField
                    label={t('codebookLibrary.text')}
                    value={code.text}
                    onChange={(e) => updateCode(index, 'text', e.target.value)}
                    size="small"
                    sx={{ flexGrow: 1, background: '#fff' }}
                    disabled={isLoading}
                  />
                  <IconButton 
                    onClick={() => deleteCode(index)}
                    disabled={isLoading}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
            
            <Box sx={{ display: 'flex', gap: 2, mt: 2, ml: 2 }}>
              <Button 
                variant="contained" 
                onClick={addCode}
                disabled={isLoading}
              >
                {t('codebookLibrary.addCode')}
              </Button>            
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button 
                variant="contained" 
                onClick={saveCodebook}
                disabled={isLoading}
                sx={{ ml: 2, mb: 2 }}
              >
                {isLoading ? <CircularProgress size={24} /> : t('codebookLibrary.saveCodebook')}
              </Button>
              {codebooks.some(cb => cb.id === editingCodebook.id) ? (
                <Button 
                  variant="outlined" 
                  color="error"
                  onClick={handleDeleteConfirmation}
                  disabled={isLoading}
                  sx={{ mb: 2 }}
                >
                  {t('codebookLibrary.deleteCodebook')}
                </Button>
              ) : (
                <Button 
                  variant="outlined" 
                  onClick={handleCancel}
                  disabled={isLoading}
                  sx={{ mb: 2 }}
                >
                  {t('cancel')}
                </Button>
              )}
            </Box>
          </Box>
        )}

        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
          <DialogTitle>{t('codebookLibrary.confirmDeleteTitle')}</DialogTitle>
          <DialogContent>
            {t('codebookLibrary.confirmDeleteMessage')}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleDeleteCodebook} color="error" variant="contained">
              {t('delete')}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        >
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default CodebookLibraryPage;